import { capitalize } from "es-toolkit/string";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { NumberUtils } from "@stellar/utils";
import {
  SCENE_SEATS_DISPLAY_NAME,
  SCENE_SEAT_DISPLAY_NAME,
} from "@components/plans-table/scene-licenses";

const THOUSAND = 1000;

/** The unit of storage for limits */
export type LimitStorageUnit = "GB" | "TB";

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const GIGA_BYTE_SIZE = 1024 ** 3;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const TERA_BYTE_SIZE = 1024 ** 4;

/** Name to display for credits */
export const CREDITS_DISPLAY_NAME = "tokens";

/** Name to display for a single credit */
const CREDIT_DISPLAY_NAME = "token";

/** Used when as plan has no set limits */
export const UNLIMITED = "Unlimited";

/** Used when an exception occurs */
const UNDEFINED = "Undefined";

/** The names of a limit type, so that it can agree with the number it is shown with */
interface LimitTypeNames {
  /** Name used for a single unit of the limit, e.g. "1 Project" */
  singular: string;

  /** Name used for any other number of units, e.g. "3 Projects" */
  plural: string;
}

/** @returns the names of a limit type, or null for a type this app does not know yet */
function limitTypeNames(
  limitType: FaroCoreApiTypes.LimitType
): LimitTypeNames | null {
  switch (limitType) {
    case "PROJECT_AREA":
      return { singular: "sqft", plural: "sqft" };

    case "PROJECT_COUNT":
      return { singular: "Project", plural: "Projects" };

    case "USER_COUNT":
      return { singular: "User", plural: "Users" };

    case "WAYPOINT":
      return { singular: "Capture Location", plural: "Capture Locations" };

    case "TIMETRAVEL_ON_WAYPOINT":
      return {
        singular: "Capture per Location",
        plural: "Captures per Location",
      };

    case "STORAGE":
      return { singular: "Storage", plural: "Storage" };

    case "USER_ROLE":
      return { singular: "Active User", plural: "Active Users" };

    case "ACTIVE_SEAT":
      return {
        singular: "Sphere XG seat (Active User)",
        plural: "Sphere XG seats (Active Users)",
      };

    case "SCENE_SEAT":
      return {
        singular: SCENE_SEAT_DISPLAY_NAME,
        plural: SCENE_SEATS_DISPLAY_NAME,
      };

    case "CREDITS":
      return {
        singular: capitalize(CREDIT_DISPLAY_NAME),
        plural: capitalize(CREDITS_DISPLAY_NAME),
      };

    default:
      return null;
  }
}

/**
 * Returns a formatted name based on the limit type. Pass the number the name is shown with, so that
 * a single unit reads "1 Project" instead of "1 Projects". Without it the name stays plural.
 */
export function formatLimitType(
  limitType: FaroCoreApiTypes.LimitType,
  count?: number
): string {
  const names = limitTypeNames(limitType);

  // The backend can introduce new limit types before this app knows them,
  // so fall back to a humanized version of the raw type, e.g. "NEW_TYPE" -> "New type"
  if (!names) {
    // eslint-disable-next-line no-console -- Flag limit types the backend sends but this app doesn't handle yet
    console.error(`Unknown limit type: ${String(limitType)}`);
    return capitalize(String(limitType).replaceAll("_", " "));
  }

  return count === 1 ? names.singular : names.plural;
}

/** Format the limit value by adding both the current and maximum values */
export function formatCurrentAndMaxValueLimit(
  limit: FaroCoreApiTypes.BasicLimit
): string {
  if (limit.type === "STORAGE") {
    return formatStorageValues(limit);
  } else {
    return `${limit.data.current || 0} | ${limit.data.max} ${formatLimitType(
      limit.type,
      limit.data.max
    )}`;
  }
}

/** Format the limit value by only showing the max value */
export function formatLimitValue({
  type,
  value,
}: FaroCoreApiTypes.LimitPayload): string {
  if (type === "STORAGE") {
    const unit = getStorageUnit(value);
    const maxValue = formatStorageMaxValue(value, unit);
    return `${maxValue} ${unit}`;
  } else {
    return `${NumberUtils.formatNumberWithSpaces(value)} ${formatLimitType(
      type,
      value
    )}`;
  }
}

/** Returns the unit based on the maximum value. If max value is undefined or small than 1 TB, then it return GB, otherwise TB */
export function getStorageUnit(maxBytes?: number): LimitStorageUnit {
  if (maxBytes === undefined || maxBytes < 0) {
    return "GB";
  }
  if (maxBytes < THOUSAND * GIGA_BYTE_SIZE) {
    return "GB";
  } else {
    return "TB";
  }
}

function formatStorageCurrentValue(
  maxBytes: number | undefined,
  unit: LimitStorageUnit
): string {
  if (maxBytes === undefined || maxBytes < 0) {
    return "0";
  }

  if (unit === "GB") {
    // Convert bytes to gigabytes and format to two decimal places, removing trailing zeros, e.g. 12.34000 -> 12.34
    return (maxBytes / GIGA_BYTE_SIZE).toFixed(2).replace(/\.?0+$/, "");
  } else {
    return (maxBytes / TERA_BYTE_SIZE).toFixed(2).replace(/\.?0+$/, "");
  }
}

/**
 * Formats the max value from bytes to gigabytes, applying thousand separators.
 * If the value is not defined, it defaults to 0.
 *
 * @param maxBytes - The input value in bytes to be converted and formatted.
 * @returns A string and unit representing the formatted value in unit with thousand separators.
 */
export function formatStorageMaxValue(
  maxBytes: number | undefined,
  unit: LimitStorageUnit
): string {
  if (maxBytes === undefined || maxBytes < 0) {
    return "0";
  }
  if (unit === "GB") {
    return NumberUtils.formatNumberWithSpaces(maxBytes / GIGA_BYTE_SIZE);
  } else {
    return NumberUtils.formatNumberWithSpaces(maxBytes / TERA_BYTE_SIZE);
  }
}

/**
 * Formats storage type limit values into a human-readable string.
 * Converts values from bytes to gigabytes, applies thousand separators,
 * and formats the current and maximum values for display, appending a 'GB' unit label.
 * the current value is formatted with up to four decimal places, removing trailing zeros.
 *
 * @param limit - The limit payload containing storage data to be formatted.
 * @returns A string representing the formatted current and maximum storage values.
 */
export function formatStorageValues(
  limit: FaroCoreApiTypes.BasicLimit
): string {
  const unit = getStorageUnit(limit.data.max);
  const currentValue = formatStorageCurrentValue(limit.data.current, unit);
  const maxValue = formatStorageMaxValue(limit.data.max, unit);

  if (maxValue === "0" && currentValue !== "0") {
    return UNDEFINED;
  }

  return `${currentValue} | ${maxValue} ${unit}`;
}
